import { useEffect, useMemo, useState } from "react";
import {
  getCityHasAirportDefault,
  getCityHasOneAirportByCode,
  searchCityHasAirportByKeyword,
} from "./utils";
import { useMediaQuery } from "@mui/material";
import { CoreCityHasAirport } from "./type";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { CornerDownRight, MapPin, Plane, X } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerTrigger } from "../ui/drawer";
import useDebounce from "@/hooks/useDebounce";
import { cn } from "@/utils/utils";

interface Props {
  title: string;
  airport: string;
  placeholder: string;
  onChange: (value: string) => void;
}

const SelectAirport: React.FC<Props> = ({
  title,
  airport,
  placeholder,
  onChange,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const citySelected = useMemo(() => {
    return airport ? getCityHasOneAirportByCode(airport) : null;
  }, [airport]);
  const [keyword, setKeyword] = useState<string>("");
  const [result, setResult] = useState<CoreCityHasAirport[]>([]);
  const isDesktop = useMediaQuery("(min-width: 769px)");
  const debounceKeyword = useDebounce(keyword, 300);

  useEffect(() => {
    if (debounceKeyword) {
      setResult(searchCityHasAirportByKeyword(debounceKeyword));
    } else {
      setResult(getCityHasAirportDefault());
    }
  }, [debounceKeyword]);

  const handleChange = (code: string) => {
    onChange(code);
    setOpen(false);
  };

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex w-full items-center gap-3 rounded-lg border border-[#E2E8F0] px-4 py-[14px] text-[14px]/[24px] font-semibold text-[#94A3B8]">
            <MapPin className="size-5" />
            <span className={cn({"text-text": !!citySelected})}>
              {citySelected ? (
                <>
                  {citySelected.airport
                    ? `${citySelected.name} - ${citySelected.airport.code}`
                    : `${citySelected.name} - ${citySelected.code}`}
                </>
              ) : (
                placeholder
              )}
            </span>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[500px] rounded-md border border-[#E2E8F0] px-0 py-6"
        >
          <div className="flex flex-col gap-3 px-6 pb-3">
            {/* <div className="text-[16px]/[24px] font-semibold">{title}</div> */}
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className="placeholder:text-[#94A3B8] h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-text outline-none placeholder:text-sm placeholder:font-normal"
            />
            <div className="text-[14px]/[24px] font-normal text-[#1E293B]">
              Type more to get more relevant results
            </div>
          </div>
          <div className="h-[380px] overflow-y-auto">
            {result.map((item) => {
              if (item.airports.length === 1) {
                return (
                  <button
                    onClick={() => handleChange(item.code)}
                    key={item.code}
                    className="flex w-full items-center justify-between px-6 py-4"
                  >
                    <div className="flex items-center gap-1">
                      <Plane className="size-5 text-[#475569]" />
                      <div className="flex flex-col">
                        <span className="text-xs font-medium text-[#64748B]">
                          {item.name}
                        </span>
                        <span className="text-[16px]/[24px] font-semibold text-[#475569]">
                          {item.airports[0].name}
                        </span>
                      </div>
                    </div>
                    <span className="rounded-full border border-[#475569] px-3 py-2 text-xs font-medium text-[#475569]">
                      {item.code}
                    </span>
                  </button>
                );
              } else {
                return (
                  <div key={item.code}>
                    <button
                      className="flex w-full items-center justify-between bg-[#EEF2FF] px-6 py-4"
                      onClick={() => handleChange(item.code)}
                    >
                      <div className="flex items-center gap-1">
                        <MapPin className="size-5 text-[#475569]" />
                        <span className="text-lg font-semibold text-[#475569]">
                          {item.name}
                        </span>
                        <span className="text-[16px]/[24px] font-normal text-[#475569]">
                          (All Airports)
                        </span>
                      </div>
                      <span className="rounded-full border border-[#475569] px-3 py-2 text-xs font-medium text-[#475569]">
                        {item.code}
                      </span>
                    </button>
                    {item.airports.map((childItem) => (
                      <button
                        className="flex w-full items-center justify-between px-6 py-4"
                        onClick={() => handleChange(childItem.code)}
                        key={childItem.code}
                      >
                        <div className="flex items-center gap-1">
                          <CornerDownRight className="size-5 text-[#94A3B8]" />
                          <div className="flex flex-col">
                            <div className="flex items-center gap-1">
                              <Plane className="size-4 text-[#475569]" />
                              <span className="text-xs font-medium text-[#64748B]">
                                {item.name}
                              </span>
                            </div>
                            <span className="pl-5 text-[16px]/[24px] font-semibold text-[#475569]">
                              {childItem.name}
                            </span>
                          </div>
                        </div>
                        <span className="rounded-full border border-[#475569] px-3 py-2 text-xs font-medium text-[#475569]">
                          {childItem.code}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              }
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  }
  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <div className="flex w-full gap-3 px-4 py-[14px] text-[14px]/[24px] font-semibold text-[#94A3B8] rounded-lg border border-[#E2E8F0]">
          <span className={cn({"text-text": !!citySelected})}>
            {citySelected ? (
              <>
                {citySelected.airport
                  ? `${citySelected.name} - ${citySelected.airport.code}`
                  : `${citySelected.name} - ${citySelected.code}`}
              </>
            ) : (
              placeholder
            )}
          </span>
        </div>
      </DrawerTrigger>
      <DrawerContent className="h-[70%] p-0 [&>div.bg-muted]:hidden">
        <DrawerTitle className="flex items-center justify-between p-4 text-text">
          <span className="text-xl">{title}</span>
          <X className="size-5" onClick={() => setOpen(false)} />
        </DrawerTitle>
        <div className="flex flex-col gap-3 px-6 py-3">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="placeholder:text-[#94A3B8] h-12 rounded-lg border border-[#E2E8F0] bg-white px-3 text-text outline-none placeholder:text-sm placeholder:font-normal"
          />
          <div className="text-[14px]/[24px] font-normal text-[#1E293B]">
            Type more to get more relevant results
          </div>
        </div>
        <div className="h-[380px] overflow-y-auto">
          {result.map((item) => {
            if (item.airports.length === 1) {
              return (
                <div
                  onClick={() => handleChange(item.code)}
                  key={item.code}
                  className="flex items-center justify-between px-6 py-4"
                >
                  <div className="flex items-center gap-1">
                    <Plane className="size-5 text-[#475569]" />
                    <div className="flex flex-col">
                      <span className="text-xs font-medium text-[#64748B]">
                        {item.name}
                      </span>
                      <span className="text-[16px]/[24px] font-semibold text-[#475569]">
                        {item.airports[0].name}
                      </span>
                    </div>
                  </div>
                  <span className="rounded-full border border-[#475569] px-3 py-2 text-xs font-medium text-[#475569]">
                    {item.code}
                  </span>
                </div>
              );
            } else {
              return (
                <div key={item.code}>
                  <div
                    className="flex items-center justify-between bg-[#EEF2FF] px-6 py-4"
                    onClick={() => handleChange(item.code)}
                  >
                    <div className="flex items-center gap-1">
                      <MapPin className="size-5 text-[#475569]" />
                      <span className="text-lg font-semibold text-[#475569]">
                        {item.name}
                      </span>
                      <span className="text-[16px]/[24px] font-normal text-[#475569]">
                        (All Airports)
                      </span>
                    </div>
                    <span className="rounded-full border border-[#475569] px-3 py-2 text-xs font-medium text-[#475569]">
                      {item.code}
                    </span>
                  </div>
                  {item.airports.map((childItem) => (
                    <div
                      className="flex items-center justify-between px-6 py-4"
                      onClick={() => handleChange(childItem.code)}
                      key={childItem.code}
                    >
                      <div className="flex items-center gap-1">
                        <CornerDownRight className="size-5 text-[#94A3B8]" />
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <Plane className="size-4 text-[#475569]" />
                            <span className="text-xs font-medium text-[#64748B]">
                              {item.name}
                            </span>
                          </div>
                          <span className="pl-5 text-[16px]/[24px] font-semibold text-[#475569]">
                            {childItem.name}
                          </span>
                        </div>
                      </div>
                      <span className="rounded-full border border-[#475569] px-3 py-2 text-xs font-medium text-[#475569]">
                        {childItem.code}
                      </span>
                    </div>
                  ))}
                </div>
              );
            }
          })}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default SelectAirport;
